import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Observable, of, throwError } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { AppState } from '../../store/state/app.state';
import { environment } from 'projects/workspace/src/environments/environment';
import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { ResponseList, ResponseModel, FormInputChangedModel } from '../../shared/models';
import { FilterModel } from '../../warehouse/models/filter.model';
import { EcoOrderListTabsEnum } from '../modules/eco-orders/enums';
import { EcoOrderModel, EcoOrdersListCountersModel, ResponseEcoOrdersCountersModel } from '../modules/eco-orders/models';
import {
  DecrementLoadingRequestsCount,
  IncrementLoadingRequestsCount,
  LoadEcoOrder,
  LoadEcoOrderPositions,
  LoadEcoOrdersList,
  UpdateEcoOrder,
  UpdateEcoOrderCurrentState,
  UpdateEcoOrderUpdatedAt
} from '../modules/eco-orders/store/actions/eco-order.actions';
import { SalesPosition, SalesPositionsByProductType } from '../modules/eco-orders/models/sales-order-position.model';
import { UIStatesEnum } from 'common/src/models';
import { DEFAULT_SORT_DIRECTION } from '../../shared/constants';
import { CommonModalsActionsEnum, WarningModalComponent } from 'common/src/modules/modals/modals-common';
import { getAnotherUserEditErrorModalData } from 'common/src/modules/modals/modals-common/common-modal.config';
import { DocumentTypesUppercaseEnum } from 'common/src/modules/modals/modals-common/link-document-modal/enums/ducument-types.enum';
import { FileUploadParams } from '../../../../../../common/src/models/file-upload-params.model';
import { selectCompanyProfile } from '../../administration/store/selectors';
import { CompanyProfile } from '../../administration/models/company-profile.model';

@Injectable({
  providedIn: 'root'
})
export class ECommerceApiService {
  public wid: number;
  private readonly apiEndpoint: string = environment.javaEcommerceApiVersion;

  private apiUrl(url: string = ''): string {
    return this.apiEndpoint + url;
  }

  constructor(
    private readonly dialog: MatDialog,
    private readonly toasterService: ToasterService,
    private readonly translateService: TranslateService,
    private readonly http: HttpClient,
    private readonly store: Store<AppState>
  ) {
    this.store.select(selectCompanyProfile)
      .subscribe((profile: CompanyProfile) => {
        this.wid = profile.workspaceId;
      });
  }

  public setEcommerceProductsToShop(ids: number[]): Observable<any> {
    const body = { ids };
    return this.http.post(this.apiUrl('/admin/products/shop'), body);
  }

  public removeEcommerceProductsFromShop(ids: number[]): Observable<any> {
    const body = { ids };
    return this.http.request('delete', this.apiUrl('/admin/products/shop'), { body } );
  }

  public setEcommerceProductPrices(productId: number, priceData: any): Observable<any> {
    const body = { ...priceData };
    return this.http.request('patch', this.apiUrl(`/admin/products/price-list/${productId}`), { body } );
  }

  getECOList(
    status: EcoOrderListTabsEnum,
    page: string = '1',
    per_page: string = '100',
    sort: FilterModel = { nameColumn: 'updated_at', value: DEFAULT_SORT_DIRECTION },
    filters: any = {}
  ): Observable<ResponseList<any>> {
    const params = {
      page,
      per_page,
      [`sort[${sort.nameColumn}]`]: sort.value,
    };

    if (status === EcoOrderListTabsEnum.ALL) {
      params['filters[status]'] = [
        EcoOrderListTabsEnum.OPEN,
        EcoOrderListTabsEnum.COMPLETED,
        EcoOrderListTabsEnum.CANCELED,
        EcoOrderListTabsEnum.DELETED,
      ].join(',');
      params['filters[allStatus]'] = 'true';
    } else {
      params['filters[status]'] = status;
    }

    for (const [key, value] of Object.entries(filters)) {
      params[`filters[${key}]`] = Array.isArray(value) ? value.join(',') : value.toString();
    }

    return this.http.get<ResponseList<any>>(this.apiUrl('/admin/orders'), { params })
      .pipe(tap((data: ResponseList<any>) => {
        this.store.dispatch(LoadEcoOrdersList({
          ecoOrderListData: {
            [data.pagination.page]: {
              pagination: data.pagination,
              sort,
              search: filters.search,
              data: data.data
            }
          },
          status, page: data.pagination.page
        }));
      }));
  }

  getEcoOrdersRunpleIds(status: EcoOrderListTabsEnum): Observable<any> {
    const params = { };

    if (status === EcoOrderListTabsEnum.ALL) {
      params['status'] = [
        EcoOrderListTabsEnum.OPEN,
        EcoOrderListTabsEnum.COMPLETED,
        EcoOrderListTabsEnum.CANCELED,
        EcoOrderListTabsEnum.DELETED,
      ].join(',');
    } else {
      params['status'] = status;
    }

    return this.http.get<any>(`/sales-orders/ecommerce/runpleIds`, { params })
      .pipe(map((data: any) => data.data));
  }

  getEcoOrderListPositions(ecoId: number): Observable<SalesPosition[]> {
    return this.http.get<ResponseModel<SalesPosition[]>>(this.apiUrl(`/admin/orders/${ecoId}/list/positions`))
      .pipe(map((data: ResponseModel<SalesPosition[]>) => data.data));
  }

  getEcoOrdersCounters(): Observable<EcoOrdersListCountersModel> {
    return this.http
      .get<ResponseModel<ResponseEcoOrdersCountersModel>>(
        this.apiUrl('/admin/orders/counters')
      )
      .pipe(
        map((data: ResponseModel<ResponseEcoOrdersCountersModel>) => data.data.orders)
      );
  }

  changeEcoOrderStatusBatch(status: EcoOrderListTabsEnum, order_ids: number[]): Observable<any> {
    const body = { status, order_ids };
    return this.http.patch(this.apiUrl('/admin/orders/multiple/statuses'), body)
      .pipe(
        catchError(error => {
          this.handlePopupErrors(error);
          return throwError(error);
        })
      );
  }

  deleteEcoOrderPermanently(ecoOrderId: number): Observable<ResponseModel<null>> {
    return this.http.request<ResponseModel<null>>('delete', this.apiUrl(`/admin/orders/${ecoOrderId}`))
      .pipe(
        catchError(error => {
          this.handlePopupErrors(error);
          return throwError(error);
        })
      );
  }

  deleteEcoOrdersPermanently(ids: number[]): Observable<ResponseModel<null>> {
    const body = { ids };
    return this.http.request<ResponseModel<null>>('delete', this.apiUrl('/admin/orders'), {body})
      .pipe(
        catchError(error => {
          this.handlePopupErrors(error);
          return throwError(error);
        })
      );
  }

  getECOById(id: string | number): Observable<EcoOrderModel> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http
      .get<ResponseModel<EcoOrderModel>>(this.apiUrl(`/admin/orders/${id}`))
      .pipe(
        tap((response: ResponseModel<EcoOrderModel>) => {
          this.store.dispatch(LoadEcoOrder({ ecoOrder: response.data }));
          // this.store.dispatch(UpdateShouldRefreshEntity({ isShouldRefresh: false }));
          // this.store.dispatch(UpdateSalesOrderPositionsCount({ positionsCount: response.data.positionsCount }));
          this.store.dispatch(UpdateEcoOrderUpdatedAt({updatedAt: new Date()}));
        }),
        map((response: ResponseModel<EcoOrderModel>) => response.data),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount()))
      );
  }

  updateEcoOrder(ecoOrderId: number, field: FormInputChangedModel): Observable<EcoOrderModel> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.patch<ResponseModel<EcoOrderModel>>(this.apiUrl(`/admin/orders/${ecoOrderId}`), field)
      .pipe(
        tap((response: ResponseModel<EcoOrderModel>) => {
          this.store.dispatch(UpdateEcoOrderUpdatedAt({updatedAt: new Date()}));
          this.store.dispatch(LoadEcoOrder({ ecoOrder: response.data }));
        }),
        map((response: ResponseModel<EcoOrderModel>) => response.data),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
        catchError(error => {
          this.handlePopupErrors(error, ecoOrderId);
          return throwError(error);
        })
      );
  }

  public ecoOrderSetEdit(id: string | number, force = false): Observable<EcoOrderModel> {
    const body = { force };

    return this.http.put<ResponseModel<EcoOrderModel>>(this.apiUrl(`/admin/orders/${id}/locking`), body)
      .pipe(
        tap((response: ResponseModel<EcoOrderModel>) => {
          this.store.dispatch(UpdateEcoOrderCurrentState({ currentState: UIStatesEnum.EDIT }));
          this.store.dispatch(UpdateEcoOrder({ ecoOrder: response.data }));
        }),
        map((response: ResponseModel<EcoOrderModel>) => response.data),
        catchError(error => {
          this.handlePopupErrors(error, +id);
          return throwError(error);
        })
      );
  }

  public ecoOrderUnsetEdit(id: string | number): Observable<EcoOrderModel> {
    return this.http.request<ResponseModel<EcoOrderModel>>('put', this.apiUrl(`/admin/orders/${id}/unlocking`))
      .pipe(
        tap((response: ResponseModel<EcoOrderModel>) => {
          this.store.dispatch(UpdateEcoOrderCurrentState({ currentState: UIStatesEnum.VIEW }));
          this.store.dispatch(UpdateEcoOrder({ ecoOrder: response.data }));
        }),
        map((response: ResponseModel<EcoOrderModel>) => response.data),
        catchError(error => {
          this.handlePopupErrors(error, +id);
          return throwError(error);
        })
      );
  }

  changeEcoOrderStatus(status: EcoOrderListTabsEnum, ecoOrderId: number): Observable<EcoOrderModel> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    const body = { status };
    return this.http
      .patch(this.apiUrl(`/admin/orders/${ecoOrderId}/status`), body)
      .pipe(
        tap((response: ResponseModel<EcoOrderModel>) => {
          this.store.dispatch(UpdateEcoOrderUpdatedAt({updatedAt: new Date()}));
          this.store.dispatch(LoadEcoOrder({ ecoOrder: response.data }));
        }),
        map((data: ResponseModel<EcoOrderModel>) => data.data),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount()))
      );
  }

  getEcoOrderPositions(ecoOrderId: number): Observable<ResponseModel<SalesPositionsByProductType>> {
    return this.http.get<ResponseModel<SalesPositionsByProductType>>(this.apiUrl(`/admin/orders/${ecoOrderId}/positions`))
      .pipe(
        tap((response: ResponseModel<SalesPositionsByProductType>) => {
          this.store.dispatch(LoadEcoOrderPositions({positions: response}));
        }),
      );
  }

  createEcoOrderPosition(toId: number, position: SalesPosition): Observable<ResponseModel<SalesPositionsByProductType>> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.post<ResponseModel<SalesPositionsByProductType>>(this.apiUrl(`/admin/orders/${toId}/positions`), position)
      .pipe(
        tap(() => {
          this.store.dispatch(UpdateEcoOrderUpdatedAt({updatedAt: new Date()}));
        }),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
      );
  }

  updateEcoOrderPosition(ecoOrderId: number, positionId: number, field: FormInputChangedModel): Observable<ResponseModel<SalesPosition>> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.patch<ResponseModel<SalesPosition>>(this.apiUrl(`/admin/orders/${ecoOrderId}/positions/${positionId}`), field)
      .pipe(
        tap(() => {
          this.store.dispatch(UpdateEcoOrderUpdatedAt({updatedAt: new Date()}));
        }),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
        catchError(error => {
          this.handlePopupErrors(error, +ecoOrderId);
          return throwError(error);
        })
      );
  }

  deleteEcoOrderPositions(ecoOrderId: number, ids: number[]): Observable<ResponseModel<null>> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    const body = { ids: ids };
    return this.http.request<ResponseModel<null>>(
      'delete',
      this.apiUrl(`/admin/orders/${ecoOrderId}/positions`),
      {body}
    );
  }

  public getEcoListExport(status: string, isShop: boolean): Observable<FileUploadParams> {
    const fileParams: FileUploadParams = {
      url: this.apiUrl(`/${status}/csv?shop=${String(+isShop)}`),
      type: 'zip',
    };
    return of(fileParams);
  }

  public getEcoListExportAll(isShop: boolean): Observable<FileUploadParams> {
    const fileParams: FileUploadParams = {
      url: this.apiUrl(`/csv?shop=${String(+isShop)}`),
      type: 'zip',
    };
    return of(fileParams);
  }

  public getEcoExportParams(id: number, title: string): Observable<FileUploadParams> {
    const fileParams: FileUploadParams = {
      url: `accounting/v1/pdf/sales-order/${id}`,
      type: 'pdf',
      title,
    };
    return of(fileParams);
  }

  public exportEcoOrdersPdfBatchUrl(): string {
    return `${environment.javaApiVersion}/pdf/generated/batch?wid=${this.wid}&documentType=${DocumentTypesUppercaseEnum.SO}`;
  }

  public showMsg(type: string, message: string): void {
    this.toasterService.notify({ type, message });
  }

  private handlePopupErrors(error: HttpErrorResponse, ecoId?: string | number): void {
    error.error.errors.forEach(errorText => {
      switch (errorText) {
        case 'anotherUserEditError':
          {
            const dialog = this.dialog.open(WarningModalComponent, {
              data: getAnotherUserEditErrorModalData(
                {
                  document: DocumentTypesUppercaseEnum.ECO_ORDER,
                  userName: error.error.data.userName,
                },
                this.translateService
              )
            });

            dialog.afterClosed().subscribe(res => {
              if (res === CommonModalsActionsEnum.CONFIRM) {
                this.ecoOrderSetEdit(+ecoId, true).subscribe();
              }
            });
          }
          break;
        case 'notEditModeError':
          const documentName = this.translateService.instant('ECOMMERCE.ORDER');
          this.showMsg('warning', this.translateService.instant('COMMON.DOC_UPDATED_BY_USER', { document: documentName }));
          this.store.dispatch(UpdateEcoOrderCurrentState({ currentState: UIStatesEnum.VIEW }));
          this.getECOById(ecoId).subscribe();
          break;
        default:
          this.showMsg('error', errorText);
          break
      }
    });
  }

}
